import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useIndianStates } from '../hooks/useTax';
import taxService from '../services/taxService';

const { FiCalculator, FiRefreshCw } = FiIcons;

const TaxCalculator = () => {
  const [calculation, setCalculation] = useState(null);
  const states = useIndianStates();
  
  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: {
      amount: '',
      gst_rate: 18,
      client_type: 'B2B',
      supplier_state: '27', // Maharashtra
      client_state: '27'
    }
  });

  const watchedValues = watch();

  const handleCalculate = (data) => {
    const supplierState = states.find(s => s.code === data.supplier_state);
    const clientState = states.find(s => s.code === data.client_state);
    
    const result = taxService.calculateGST(
      parseFloat(data.amount),
      parseFloat(data.gst_rate),
      data.supplier_state,
      data.client_state,
      data.client_type === 'B2B'
    );

    setCalculation({
      ...result,
      amount: parseFloat(data.amount),
      gst_rate: parseFloat(data.gst_rate),
      supplier_state: supplierState?.name,
      client_state: clientState?.name,
      is_interstate: data.supplier_state !== data.client_state,
      client_type: data.client_type
    });
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiCalculator} className="w-6 h-6 mr-2 text-blue-600" />
            GST Calculator
          </h3>
          <p className="text-gray-600 mt-1">Calculate CGST, SGST, and IGST based on location and client type</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <div>
          <form onSubmit={handleSubmit(handleCalculate)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Taxable Amount *
              </label>
              <input
                type="number"
                step="0.01"
                {...register('amount', { 
                  required: 'Amount is required',
                  min: { value: 0.01, message: 'Amount must be greater than 0' }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter amount"
              />
              {errors.amount && (
                <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GST Rate (%)
              </label>
              <select
                {...register('gst_rate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={0}>0% (Exempt)</option>
                <option value={5}>5%</option>
                <option value={12}>12%</option>
                <option value={18}>18%</option>
                <option value={28}>28%</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Type
              </label>
              <select
                {...register('client_type')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="B2B">B2B (Business to Business)</option>
                <option value="B2C">B2C (Business to Consumer)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier State
              </label>
              <select
                {...register('supplier_state')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {states.map(state => (
                  <option key={state.code} value={state.code}>
                    {state.name} ({state.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client State
              </label>
              <select
                {...register('client_state')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {states.map(state => (
                  <option key={state.code} value={state.code}>
                    {state.name} ({state.code})
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiCalculator} className="w-4 h-4" />
              <span>Calculate GST</span>
            </button>
          </form>
        </div>

        {/* Results */}
        <div>
          {calculation ? (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Calculation Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Amount:</p>
                    <p className="font-semibold">{formatCurrency(calculation.amount)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">GST Rate:</p>
                    <p className="font-semibold">{calculation.gst_rate}%</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Tax Type:</p>
                    <p className="font-semibold">{calculation.tax_type}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Transaction:</p>
                    <p className="font-semibold">{calculation.is_interstate ? 'Inter-State' : 'Intra-State'}</p>
                  </div>
                </div>
              </div>

              {calculation.client_type === 'B2B' && calculation.gst_rate > 0 ? (
                <div className="space-y-3">
                  {calculation.is_interstate ? (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-blue-800">IGST ({calculation.gst_rate}%):</span>
                        <span className="font-bold text-blue-800">{formatCurrency(calculation.igst)}</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-green-800">CGST ({calculation.gst_rate / 2}%):</span>
                          <span className="font-bold text-green-800">{formatCurrency(calculation.cgst)}</span>
                        </div>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-purple-800">SGST ({calculation.gst_rate / 2}%):</span>
                          <span className="font-bold text-purple-800">{formatCurrency(calculation.sgst)}</span>
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="bg-gray-100 rounded-lg p-4 border-2 border-gray-300">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-800">Total Tax:</span>
                      <span className="font-bold text-lg text-gray-800">{formatCurrency(calculation.total_tax)}</span>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-yellow-800">Total Amount (Including Tax):</span>
                      <span className="font-bold text-xl text-yellow-800">
                        {formatCurrency(calculation.amount + calculation.total_tax)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
                  <p className="text-gray-600">
                    {calculation.client_type === 'B2C' 
                      ? 'No GST applicable for B2C transactions'
                      : 'No GST (Exempt service)'
                    }
                  </p>
                  <div className="mt-3">
                    <span className="font-bold text-lg text-gray-800">
                      Total Amount: {formatCurrency(calculation.amount)}
                    </span>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h5 className="font-semibold text-blue-800 mb-2">Location Details</h5>
                <div className="text-sm text-blue-700">
                  <p><span className="font-medium">Supplier:</span> {calculation.supplier_state}</p>
                  <p><span className="font-medium">Client:</span> {calculation.client_state}</p>
                  <p><span className="font-medium">Type:</span> {calculation.client_type}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-center">
                <SafeIcon icon={FiCalculator} className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">Enter amount and click calculate to see GST breakdown</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default TaxCalculator;